import React from "react";
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Trash2, Plus } from "lucide-react";
import { UseFormReturn, useFieldArray } from "react-hook-form";
import { useIncrementOptions } from "../../hooks/useIncrementOptions";
import { AgeGroupVariationFormData } from "../schemas/ageGroupVariationSchema";

export interface AlternativePricingFieldsProps {
  form: UseFormReturn<AgeGroupVariationFormData>;
}

export const AlternativePricingFields: React.FC<AlternativePricingFieldsProps> = ({
  form
}) => {
  const { incrementOptions, loading } = useIncrementOptions();
  const pricingTiers = form.watch("pricingTiers") as any;
  const numTiers: 0 | 2 | 3 = pricingTiers?.numTiers === 2 ? 2 : pricingTiers?.numTiers === 3 ? 3 : 0;
  
  // Initialize field array for alternative pricing options
  const fieldArrayResult = useFieldArray({
    control: form.control,
    name: "regularOfferIncrementAlternatives"
  });
  
  const { fields, append, remove } = fieldArrayResult;
  
  console.log("[AlternativePricingFields] Field array initialized successfully with", fields.length, "fields");

  const handleAddAlternative = () => {
    try {
      append({
        key: "oi_none",
        price: 0,
        note: ""
      });
    } catch (error) {
      console.error("[AlternativePricingFields] Error adding alternative:", error);
    }
  };

  const handleRemoveAlternative = (index: number) => {
    try {
      remove(index);
    } catch (error) {
      console.error("[AlternativePricingFields] Error removing alternative:", error);
    }
  };

  // Get already used keys to prevent duplicates - add error handling
  let usedKeys;
  try {
    usedKeys = new Set([
      // Include the main program's increment key
      form.watch("regularOfferIncrementKey"),
      // Include all alternative keys
      ...fields.map((_, index) => form.watch(`regularOfferIncrementAlternatives.${index}.key`))
    ].filter(Boolean));
  } catch (error) {
    console.error("[AlternativePricingFields] Error getting used keys:", error);
    usedKeys = new Set();
  }

  console.log("[AlternativePricingFields] Rendering with", fields.length, "alternatives");

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h4 className="text-md font-medium text-left">Alternative lengths and pricing</h4>
          <p className="text-sm text-muted-foreground text-left">
            Offer the same program with different session lengths and pricing
          </p>
        </div>
        <Button 
          type="button" 
          variant="outline" 
          size="sm"
          onClick={handleAddAlternative}
          className="flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add alternative length and pricing
        </Button>
      </div>
      
      {fields.length === 0 && (
        <div className="text-center py-8 text-muted-foreground border-2 border-dashed border-gray-200 rounded-lg">
          <p>No alternative lengths added yet.</p>
          <p className="text-sm mt-1">Click the button above to add different session lengths with their own pricing.</p>
        </div>
      )}

      {/* Dynamic rows for each alternative */}
      {fields.map((field, index) => (
        <div key={field.id} className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 p-4 border rounded-lg bg-gray-50">
          {/* Column 1: Alternative Length */}
          <FormField
            control={form.control}
            name={`regularOfferIncrementAlternatives.${index}.key`}
            render={({ field: formField }) => {
              // Add debugging for Select values
              console.log(`[AlternativePricingFields] Alternative ${index} - formField.value:`, formField.value);
              console.log(`[AlternativePricingFields] Alternative ${index} - incrementOptions:`, incrementOptions);
              
              return (
                <FormItem>
                  <FormLabel>Alternative length</FormLabel>
                  <Select 
                    disabled={loading} 
                    onValueChange={formField.onChange} 
                    value={formField.value || "oi_none"} // Ensure no empty values
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select length" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {loading ? (
                        <SelectItem value="loading" disabled>Loading...</SelectItem>
                      ) : (
                        incrementOptions
                          .filter(option => 
                            // Allow the current selection or options not already used
                            // Filter out empty values to prevent Select errors
                            option.value && option.value.trim() !== "" && 
                            (option.value === formField.value || !usedKeys.has(option.value))
                          )
                          .map(option => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
          
          {/* Column 2: Regular Price */}
          <FormField
            control={form.control}
            name={`regularOfferIncrementAlternatives.${index}.price`}
            render={({ field: formField }) => (
              <FormItem>
                <FormLabel>
                  {numTiers > 0 ? "Tier 1 regular price (USD)" : "Regular price for this length (USD)"}
                </FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    max="99999"
                    {...formField}
                    value={formField.value || ""}
                    onChange={e => {
                      const val = e.target.value === "" ? 0 : parseFloat(e.target.value);
                      formField.onChange(val);
                    }}
                  />
                </FormControl>
                <FormMessage />

                {numTiers >= 2 && (
                  <div className="mt-4">
                    <FormField
                      control={form.control}
                      name={`regularOfferIncrementAlternatives.${index}.priceTier2`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Tier 2 regular price (USD)</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              placeholder="0.00"
                              step="0.01"
                              min="0"
                              max="99999"
                              {...field}
                              value={field.value ?? ""}
                              onChange={e => {
                                const val = e.target.value === "" ? null : parseFloat(e.target.value);
                                field.onChange(val);
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                )}

                {numTiers >= 3 && (
                  <div className="mt-4">
                    <FormField
                      control={form.control}
                      name={`regularOfferIncrementAlternatives.${index}.priceTier3`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Tier 3 regular price (USD)</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              placeholder="0.00"
                              step="0.01"
                              min="0"
                              max="99999"
                              {...field}
                              value={field.value ?? ""}
                              onChange={e => {
                                const val = e.target.value === "" ? null : parseFloat(e.target.value);
                                field.onChange(val);
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                )}
              </FormItem>
            )}
          />
          
          {/* Column 3: Price Footnote with Delete */}
          <FormField
            control={form.control}
            name={`regularOfferIncrementAlternatives.${index}.note`}
            render={({ field: formField }) => (
              <FormItem>
                <div className="flex items-center justify-between">
                  <FormLabel>Price footnote (optional)</FormLabel>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveAlternative(index)}
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <FormControl>
                  <Textarea 
                    placeholder="Optional pricing note"
                    className="min-h-[60px] resize-none"
                    maxLength={200}
                    {...formField}
                  />
                </FormControl>
                <FormMessage />
                <p className="text-sm text-muted-foreground mt-1">
                  {formField.value?.length || 0}/200 characters
                </p>
              </FormItem>
            )}
          />
        </div>
      ))}
    </div>
  );
}; 